import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiDollarSign, FiTag, FiCalendar, FiRepeat, FiAlignLeft } = FiIcons;

const CATEGORIES = [
  'Lebensmittel', 'Wohnen', 'Transport', 'Versicherung', 
  'Freizeit', 'Sparen', 'Sonstiges', 'Gehalt', 'Geschenk', 
  'Restaurant', 'Kleidung', 'Gesundheit'
];

export default function TransactionModal({ transaction, onClose, onSave }) {
  const [formData, setFormData] = useState({
    amount: '',
    type: 'expense', // 'income' or 'expense'
    category: 'Sonstiges',
    description: '',
    date: new Date().toISOString().split('T')[0],
    isRecurring: false,
    recurrenceInterval: 'monthly'
  });

  useEffect(() => {
    if (transaction) {
      setFormData({
        ...transaction,
        date: transaction.date ? new Date(transaction.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
      });
    }
  }, [transaction]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      amount: parseFloat(formData.amount)
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} 
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {transaction ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Type Selection */}
            <div className="flex p-1 bg-gray-100 dark:bg-gray-700 rounded-lg mb-4">
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                  formData.type === 'expense' 
                    ? 'bg-white dark:bg-gray-600 text-red-600 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                }`}
                onClick={() => setFormData({ ...formData, type: 'expense' })}
              >
                Ausgabe
              </button>
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                  formData.type === 'income' 
                    ? 'bg-white dark:bg-gray-600 text-green-600 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                }`}
                onClick={() => setFormData({ ...formData, type: 'income' })}
              >
                Einnahme
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Betrag (€) *</label>
              <div className="relative">
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={e => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full pl-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kategorie</label>
              <div className="relative">
                <SafeIcon icon={FiTag} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <select
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full pl-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Datum</label>
              <div className="relative">
                <SafeIcon icon={FiCalendar} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                  className="w-full pl-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beschreibung</label>
              <div className="relative">
                <SafeIcon icon={FiAlignLeft} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full pl-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="Wofür?"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <input
                type="checkbox"
                id="isRecurring"
                checked={formData.isRecurring}
                onChange={e => setFormData({ ...formData, isRecurring: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isRecurring" className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <SafeIcon icon={FiRepeat} className="w-4 h-4 mr-1 text-gray-500" />
                Wiederkehrend
              </label>
            </div>

            {formData.isRecurring && (
               <div className="ml-6">
                 <select
                   value={formData.recurrenceInterval}
                   onChange={e => setFormData({ ...formData, recurrenceInterval: e.target.value })}
                   className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-2 py-1"
                 >
                   <option value="monthly">Monatlich</option>
                   <option value="weekly">Wöchentlich</option>
                   <option value="yearly">Jährlich</option>
                 </select>
               </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">
                Abbrechen
              </button>
              <button type="submit" className={`px-4 py-2 text-white rounded-lg ${formData.type === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                Speichern
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}